import { useQueryClient } from '@tanstack/react-query';
import { type Event, createEvent as createEventModel } from 'models';
import { useMutation } from 'query';
import { handleAxiosError } from 'ui';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { getCalendarPrincipalEventsListQueryKey } from 'features/calendar/queries';
import { editEvent } from '../../services';
import { getNodeEventDetailQueryKey, getNodeEventsQueryKey } from '../keys';

export type EditEventRequest = {
  data: EditEventData;
  eventId: string;
};

type EditBaseEventData = {
  endTime: string;
  html?: string;
  name: string;
  startTime: string;
  text?: string;
};

type EditChannelEventData = {
  featuredInChannels?: boolean;
  organization?: string;
  showInChannel?: boolean;
  tags?: string[];
};

type EditEventData = EditReminderEventData | EditVideoConferenceEventData | EditWebinarEventData;

type EditReminderEventData = {
  allDay: boolean;
  type: 'reminder';
} & EditBaseEventData;

type EditVideoConferenceEventData = {
  channel: EditChannelEventData;
  type: 'video_conference';
} & EditBaseEventData;

type EditWebinarEventData = {
  channel: EditChannelEventData;
  type: 'webinar';
} & EditBaseEventData;

const defaultMutateOnError = (error: ApiClientResponseError<ResponseErrorDataObject>) => {
  handleAxiosError(error, 'Event cannot be edited, please try again later.');
};

export const useEditEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Event, EditEventRequest>({
    defaultMutateOnError,
    async mutationFn({ data, eventId }) {
      const response = await editEvent({
        body: { ...data },
        routeParams: { id: eventId },
      });

      return createEventModel(response.data);
    },
    async onSettled(_, __, variables) {
      await queryClient.invalidateQueries({
        queryKey: getNodeEventDetailQueryKey({ eventId: variables.eventId }),
      });
      await queryClient.invalidateQueries({ queryKey: getNodeEventsQueryKey({}) });
      await queryClient.invalidateQueries({ queryKey: getCalendarPrincipalEventsListQueryKey({}) });
    },
  });
};
